// ────────────────────────────────────────────────────────────────────────────
//  useOrg.ts — Organisation + Mitglieder + Beitritt
//
//  Worker-Endpunkte:
//    GET  /org                  — Detail (404 wenn nicht zugewiesen)
//    GET  /org/members          — Liste der aktiven Mitglieder
//    GET  /org/preview-code?code=XXX — Vorschau eines Invite-Codes
//    POST /org/join {code}      — Beitritt
//    POST /org/leave            — Verlassen
//    POST /onboarding/org {name} — Neue Org erstellen (verlangt Business-Lizenz
//                                  oder accountType=Organisation, sonst 403)
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, useCallback } from 'react'
import { api, APIError } from './api'

export interface OrgDetail {
  id:               number
  name:             string
  ownerId:          string
  logoUrl:          string | null
  activeExtensions: string[]
  memberCount:      number
  memberLimit?:     number
  ownerLicenseSlug?: string
  createdAt:        string
  /// Eingefroren weil Owner-Abo abgelaufen — UI zeigt großen Sperr-Banner.
  /// Mutationen liefern 423 zurück. Auftauen via Re-Subscribe.
  frozenAt?:        string | null
  frozenReason?:    string | null
}

export interface OrgMember {
  id:          number
  userId:      string
  email:       string
  displayName: string
  avatarUrl:   string | null
  orgRole:     'Owner' | 'Admin' | 'Member' | string
  joinedAt:    string
  isActive:    boolean
  permissions: {
    canManageMembers:      boolean
    canManageInviteCodes:  boolean
    canCreateGroups:       boolean
    canManageFiles:        boolean
    canInviteToChats:      boolean
    canUseMoreSpace:       boolean
    canViewSalaries:       boolean
    canManageEmployeeProfiles: boolean
    canManageOrgStructure: boolean
    canUseRecruitment:     boolean
    canManageRecruitment:  boolean
  }
}

export interface OrgPreview {
  orgId:       number
  orgName:     string
  ownerName:   string
  memberCount: number
  expiresAt:   string | null
}

let orgCache: OrgDetail | null | undefined = undefined
const orgListeners = new Set<(o: OrgDetail | null) => void>()

/// Liefert die aktive Org des eingeloggten Users — oder null wenn nicht zugewiesen.
export function useOrg() {
  const [org, setOrg] = useState<OrgDetail | null>(orgCache ?? null)
  const [loading, setLoading] = useState(orgCache === undefined)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const detail = await api.get<OrgDetail>('/org')
      orgCache = detail
      orgListeners.forEach(fn => fn(detail))
      setError(null)
    } catch (e: any) {
      if (e instanceof APIError && e.status === 404) {
        // Kein Org → null cachen
        orgCache = null
        orgListeners.forEach(fn => fn(null))
        setError(null)
      } else {
        setError(e?.message ?? 'load failed')
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const fn = (o: OrgDetail | null) => setOrg(o)
    orgListeners.add(fn)
    if (orgCache === undefined) {
      void refresh()
    } else {
      setLoading(false)
    }
    return () => { orgListeners.delete(fn) }
  }, [refresh])

  return { org, loading, error, refresh }
}

export function resetOrgCache() {
  orgCache = undefined
  orgListeners.forEach(fn => fn(null))
}

// ── Mitglieder ─────────────────────────────────────────────────────────────
export function useOrgMembers(enabled = true) {
  const [items, setItems] = useState<OrgMember[]>([])
  const [loading, setLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true); setError(null)
    try {
      const r = await api.get<{ items: OrgMember[] }>('/org/members')
      setItems(r.items ?? [])
    } catch (e: any) {
      setError(e?.message ?? 'load failed')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    if (enabled) void refresh()
  }, [enabled, refresh])

  return { items, loading, error, refresh }
}

// ── Beitritts-Helpers ──────────────────────────────────────────────────────
export async function previewInviteCode(code: string): Promise<OrgPreview> {
  return api.get<OrgPreview>(`/org/preview-code?code=${encodeURIComponent(code.trim().toUpperCase())}`)
}

export async function joinOrgWithCode(code: string): Promise<OrgDetail> {
  const detail = await api.post<OrgDetail>('/org/join', { code: code.trim().toUpperCase() })
  orgCache = detail
  orgListeners.forEach(fn => fn(detail))
  return detail
}

/// Erstellt eine neue Organisation und macht den aktuellen User zum Owner.
/// Backend-Endpoint: POST /onboarding/org { name }. Verlangt Business-Lizenz
/// oder accountType='Organisation' — sonst antwortet der Server mit 403.
export async function createOrg(name: string): Promise<OrgDetail> {
  const detail = await api.post<OrgDetail>('/onboarding/org', { name: name.trim() })
  orgCache = detail
  orgListeners.forEach(fn => fn(detail))
  return detail
}

export async function leaveOrg(): Promise<void> {
  await api.post('/org/leave', {})
  orgCache = null
  orgListeners.forEach(fn => fn(null))
}

// ── Mitglieder-Verwaltung (Owner) ──────────────────────────────────────────
export async function setMemberRole(memberId: number, role: 'Admin' | 'Member'): Promise<OrgMember> {
  return api.put<OrgMember>(`/org/members/${memberId}/role`, { role })
}

export async function setMemberPermissions(memberId: number, perms: Partial<OrgMember['permissions']>): Promise<OrgMember> {
  return api.put<OrgMember>(`/org/members/${memberId}/permissions`, { permissions: perms })
}

export async function removeMember(userId: string): Promise<void> {
  await api.delete(`/org/members/${userId}`)
}

// ── Einladungs-Codes ──────────────────────────────────────────────────────
export interface InviteCode {
  id:        number
  code:      string
  maxUses:   number | null
  usedCount: number
  expiresAt: string | null
  createdAt: string
}

function buildInviteCodeDto(row: any): InviteCode {
  return {
    id:        row.id,
    code:      row.code,
    maxUses:   row.maxUses ?? row.max_uses ?? null,
    usedCount: row.usedCount ?? row.used_count ?? 0,
    expiresAt: row.expiresAt ?? row.expires_at ?? null,
    createdAt: row.createdAt ?? row.created_at,
  }
}

export async function listInviteCodes(): Promise<InviteCode[]> {
  const res = await api.get<any>('/org/invite-codes')
  const arr = Array.isArray(res) ? res : (res?.items ?? [])
  return arr.map(buildInviteCodeDto)
}

export async function createInviteCode(opts: { maxUses?: number | null; expiresInDays?: number | null }): Promise<InviteCode> {
  const body: any = {}
  if (opts.maxUses != null)        body.maxUses       = opts.maxUses
  if (opts.expiresInDays != null) body.expiresInDays  = opts.expiresInDays
  const r = await api.post<any>('/org/invite-codes', body)
  return buildInviteCodeDto(r)
}

export async function deleteInviteCode(id: number): Promise<void> {
  await api.delete(`/org/invite-codes/${id}`)
}

// ── E-Mail-Einladung ───────────────────────────────────────────────────────
export async function sendEmailInvite(email: string, message?: string): Promise<void> {
  await api.post('/org/invite', { email, message })
}

// ── Org-Settings ───────────────────────────────────────────────────────────
export async function updateOrg(opts: { name?: string; logoUrl?: string }): Promise<void> {
  await api.put('/org', opts)
  // refresh cache
  try {
    const detail = await api.get<OrgDetail>('/org')
    orgCache = detail
    orgListeners.forEach(fn => fn(detail))
  } catch { /* ignore */ }
}

export async function deleteOrg(): Promise<void> {
  await api.delete('/org')
  orgCache = null
  orgListeners.forEach(fn => fn(null))
}
